import type { MatchState, PlayerState } from '../sim/state';
import type { SimEvent } from '../sim/events';
import type { SimConfig } from '../config/simConfig';
import type { MatchPhase, RestartKind, TeamIdx, Vec2 } from '../types';
import { PITCH } from '../config/pitchConfig';
import { kickoffTaker, placeKickoffFormation, restartTaker } from '../ai/formations';

// Phase machine helpers (§6.1). Pure — mutate `state`, emit events. The `step` pipeline drives them:
// advancePhase at step 1 (count timed phases down; expiry → next kickoff), applyRules at step 8.

/** Transition to `to`, emitting the PHASE event once (§6.1). Same-phase calls just refresh the timer. */
export function setPhase(state: MatchState, to: MatchPhase, ticksLeft: number, events: SimEvent[]): void {
  if (state.phase !== to) events.push({ t: 'PHASE', from: state.phase, to });
  // D108: the celebration belongs to GOAL_CELEBRATION and nothing else. Clearing it on every other
  // transition means a stale record can never outlive the ceremony and drive players mid-play —
  // detectGoal sets it immediately BEFORE switching in, so ordering is safe.
  if (to !== 'GOAL_CELEBRATION') {
    state.celebration = null;
    // D108a: and nobody carries the celebration out of the ceremony. The action-table timer normally
    // retires it first, but a ceremony cut short (half-end, golden goal) would otherwise strand a
    // player mid-celebration into the restart.
    for (const p of state.players) {
      if (p.action !== 'CELEBRATE') continue;
      p.action = 'IDLE';
      p.actionTicks = 0;
    }
  }
  state.phase = to;
  state.phaseTicksLeft = ticksLeft;
}

/** D83 ceremony length for a restart kind — the full soft-lock for set-pieces, a quick one for frequent
 *  low-stakes restarts. Kick-offs use the full ceremony (kickoff/goal restart = orientation time). */
export function ceremonyTicksFor(kind: RestartKind, cfg: SimConfig): number {
  if (kind === 'THROW_IN' || kind === 'KICK_IN' || kind === 'CORNER' || kind === 'GOAL_KICK' || kind === 'DROP_BALL') return cfg.rules.CEREMONY_TICKS_QUICK;
  // D122: a free kick does not reset the pitch — you are already oriented, so the full set-piece
  // cutscene was pure dead time. KICKOFF (full reset) and PENALTY (theatre) keep CEREMONY_TICKS.
  if (kind === 'FREE_KICK') return cfg.rules.CEREMONY_TICKS_FREE_KICK;
  return cfg.rules.CEREMONY_TICKS; // KICKOFF, PENALTY
}

/**
 * D155 — the TAKE-WINDOW BASE of a restart phase: the `phaseTicksLeft` value at which its ceremony ends
 * and the ball becomes takeable. It differs per phase (KICKOFF_TICKS 90 vs RESTART_MAX_TICKS 300), and
 * three places need it: `inCeremony`, the WHISTLE{RESTART} edge in `applyRules`, and the client
 * predictor, which seeds its scratch `phaseTicksLeft` so its `inCeremony` matches the server's.
 *
 * The client had its own copy and it was NOT phase-dependent — it used RESTART_MAX_TICKS always. Once
 * D141 started seeding a `kind: 'KICKOFF'` restart out of the Welcome, that made `300 > 90` true forever
 * in the prediction scratch, so `restartKickRefused` was permanently true for the kicking team: at every
 * 11v11 match start the kickoff taker pressed pass, saw nothing locally, and the ball only moved when the
 * server's correction arrived. One function, three readers, so the copies cannot drift again.
 *
 * Returns null for a phase that has no restart ceremony.
 */
export function ceremonyBaseTicks(phase: MatchState['phase'], cfg: SimConfig): number | null {
  if (phase === 'KICKOFF') return cfg.rules.KICKOFF_TICKS;
  if (phase === 'RESTART_PENDING') return cfg.rules.RESTART_MAX_TICKS;
  return null;
}

/** D83 — is the current restart still in its CEREMONY (ball untakeable)? The restart phase runs for
 *  base + ceremony ticks; the ceremony is the FIRST `ceremony` of those (phaseTicksLeft above the base),
 *  so the take/rescue window (RESTART_MAX_TICKS or KICKOFF_TICKS) is intact AFTER the ceremony ends.
 *  Derived from the existing countdown — no new state field (codec/hash untouched). */
export function inCeremony(state: MatchState, cfg: SimConfig): boolean {
  if (state.restart === null) return false;
  const base = ceremonyBaseTicks(state.phase, cfg);
  return base !== null && state.phaseTicksLeft > base;
}

/** Begin a kickoff for `kickTeam` (§6.1, reworked D43):
 *  · walled sizes (≤ WALLED_MAX_SIZE): Bolzplatz style — play restarts as the KEEPER's kick from the
 *    goal area (formations reset first, then the D38 goal-kick discipline runs);
 *  · big pitches: classic centre kickoff, but the taker STARTS IN POSSESSION with a support mate in
 *    the circle — the kicking team can dribble or lay it off from the first tick. */
export function startKickoff(state: MatchState, kickTeam: TeamIdx, events: SimEvent[], cfg: SimConfig): void {
  state.ball.pos = { x: 0, y: 0, z: 0 };
  state.ball.vel = { x: 0, y: 0, z: 0 };
  state.ball.spin = { x: 0, y: 0, z: 0 };
  state.ball.possessor = null;
  state.ball.claimCooldownTicks = 0;
  state.ball.claimCooldownOnly = null;
  state.ball.trappedTicks = 0; // symmetry with startRestart — an auto-timeout kickoff mustn't inherit stale trapped
  state.ball.shotPending = false;
  state.ball.kickedBy = null; // D62: the pre-stoppage kicker must not survive a dead ball (poisons the D59 feeder memory)
  state.ball.lastTouch = null;
  for (const p of state.players) p.offsideLiable = false; // liability clears on a dead ball (§6.3)
  placeKickoffFormation(state, kickTeam, cfg); // D34: everyone back to their slot, taker on the spot
  events.push({ t: 'WHISTLE', kind: 'KICKOFF' });

  if (state.mode.teamSize <= cfg.rules.WALLED_MAX_SIZE) {
    // D43: small-sided kickoff = the keeper brings it back into play from his goal area
    const pitch = PITCH[state.mode.teamSize];
    const spot = { x: -state.attackDir[kickTeam] * (pitch.length / 2 - pitch.goalAreaDepth), y: 0 };
    startRestart(state, events, cfg, 'GOAL_KICK', kickTeam, spot);
    return;
  }

  const taker = kickoffTaker(state, kickTeam);
  state.restart = { kind: 'KICKOFF', spot: { x: 0, y: 0 }, team: kickTeam, taker };
  if (taker !== null) {
    // D43: the taker OWNS the ball at the whistle — no loose-ball scramble on the spot
    state.ball.possessor = taker;
    state.ball.lastTouch = { entity: taker, team: kickTeam, tick: state.tick };
  }
  setPhase(state, 'KICKOFF', ceremonyTicksFor('KICKOFF', cfg) + cfg.rules.KICKOFF_TICKS, events); // D83: ceremony + take window
}

/** D34: during the KICKOFF window every player stays in their own half — the kick-team taker inside
 *  the centre circle is the only exemption. Crossing position is clamped to the line and the crossing
 *  velocity killed (they may still move freely within their half). */
export function enforceKickoffHalves(state: MatchState): void {
  if (!state.restart || state.restart.kind !== 'KICKOFF') return;
  const taker = kickoffTaker(state, state.restart.team);
  for (const p of state.players) {
    if (p.sentOff || p.id === taker) continue;
    const dir = state.attackDir[p.team];
    if (dir * p.pos.x <= 0) continue; // already own half
    p.pos.x = 0; // on the line = legal
    if (dir * p.vel.x > 0) p.vel.x = 0; // don't keep pushing across
  }
}

/** Place a dead ball for a restart (§6.1): ball to the spot, RESTART_PENDING window, OUT_OF_PLAY event. */
export function startRestart(state: MatchState, events: SimEvent[], cfg: SimConfig, kind: RestartKind, team: TeamIdx, spot: Vec2): void {
  state.ball.pos = { x: spot.x, y: spot.y, z: 0 };
  state.ball.vel = { x: 0, y: 0, z: 0 };
  state.ball.spin = { x: 0, y: 0, z: 0 };
  state.ball.possessor = null;
  state.ball.claimCooldownTicks = 0;
  state.ball.claimCooldownOnly = null;
  state.ball.trappedTicks = 0;
  state.ball.shotPending = false;
  state.ball.kickedBy = null; // D62: the pre-stoppage kicker must not survive a dead ball (poisons the D59 feeder memory)
  for (const p of state.players) p.offsideLiable = false; // liability clears on a dead ball (§6.3)
  const taker = restartTaker(state, kind, team, spot);
  state.restart = { kind, spot: { x: spot.x, y: spot.y }, team, taker };
  // D122: EVERY designated taker — penalty included — gets a kickable stance. A fouled penalty taker
  // used to keep his full 57-tick ragdoll+getup, so the one player you want on the ball spent the
  // ceremony face-down. Only the TELEPORT below is free-kick-only; `placePenalty` owns penalty
  // placement (and, since D122, only clamps it thereafter).
  if (taker !== null) {
    const t = state.players[taker]!;
    t.action = 'IDLE';
    t.actionTicks = 0;
    t.charge = null;
  }
  // D122: a PENALTY taker is placed ONCE, here, a step behind the ball facing goal. `placePenalty`
  // used to do this every tick at sub-step 8 (after movement), which is exactly why a human penalty
  // taker could not move — his input was overwritten before it ever reached the screen.
  if (taker !== null && kind === 'PENALTY') {
    const t = state.players[taker]!;
    const side = state.attackDir[team];
    t.pos = { x: spot.x - side * 0.6, y: spot.y };
    t.vel = { x: 0, y: 0 };
    t.facing = side > 0 ? 0 : Math.PI;
  }
  // D38: teleport the designated taker to the spot — no cross-pitch walk (which used to outlive the
  // pending window and turn every stoppage into a scramble). He stands a step OUTSIDE the spot facing
  // infield, clamped on-pitch.
  if (taker !== null && kind !== 'PENALTY') {
    const t = state.players[taker]!;
    const pitch = PITCH[state.mode.teamSize];
    const cl = Math.hypot(spot.x, spot.y) || 1;
    const ox = spot.x / cl; // outward: centre → spot
    const oy = spot.y / cl;
    t.pos = {
      x: clamp(spot.x + ox * 0.6, -pitch.length / 2 + 0.3, pitch.length / 2 - 0.3),
      y: clamp(spot.y + oy * 0.6, -pitch.width / 2 + 0.3, pitch.width / 2 - 0.3),
    };
    t.vel = { x: 0, y: 0 };
    t.facing = Math.atan2(-oy, -ox); // face the pitch
  }
  events.push({ t: 'OUT_OF_PLAY', restart: kind, team, spot: { x: spot.x, y: spot.y }, taker }); // D122: who takes it
  // D83: the ceremony is prepended to the take window — duration = ceremony + RESTART_MAX_TICKS, so the
  // untaken-restart auto-timeout still gives the FULL rescue window AFTER the ceremony ends.
  setPhase(state, 'RESTART_PENDING', ceremonyTicksFor(kind, cfg) + cfg.rules.RESTART_MAX_TICKS, events);
}

/** Hold the non-taking team ≥ restart clearance from the spot during a placement window (§6.1). */
export function enforceRestartClearance(state: MatchState, cfg: SimConfig): void {
  void cfg;
  if (!state.restart) return;
  const clearance = PITCH[state.mode.teamSize].restartClearance;
  const { spot, team } = state.restart;
  for (const p of state.players) {
    if (p.team === team || p.sentOff) continue; // only opponents are held out
    const dx = p.pos.x - spot.x;
    const dy = p.pos.y - spot.y;
    const d = Math.hypot(dx, dy);
    if (d >= clearance) continue;
    const nx = d > 1e-6 ? dx / d : 1;
    const ny = d > 1e-6 ? dy / d : 0;
    // D62: clamp INSIDE the playing area. step() fences the cage at sub-step 7b, BEFORE applyRules at
    // 8, so an unclamped push placed opponents up to ~4.6 m outside the boards — that state was what
    // got snapshotted and hashed, and the next tick's fence dragged them back for a visible pop.
    const half = { x: PITCH[state.mode.teamSize].length / 2 - 0.1, y: PITCH[state.mode.teamSize].width / 2 - 0.1 };
    p.pos.x = Math.max(-half.x, Math.min(half.x, spot.x + nx * clearance));
    p.pos.y = Math.max(-half.y, Math.min(half.y, spot.y + ny * clearance));
    const vn = p.vel.x * nx + p.vel.y * ny; // kill inward velocity so they don't creep back in
    if (vn < 0) {
      p.vel.x -= vn * nx;
      p.vel.y -= vn * ny;
    }
  }
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Set up a penalty for `team` (§6.2): ball on the penalty spot, attacking that team's target goal. */
export function startPenalty(state: MatchState, events: SimEvent[], cfg: SimConfig, team: TeamIdx): void {
  const pitch = PITCH[state.mode.teamSize];
  const side = state.attackDir[team];
  startRestart(state, events, cfg, 'PENALTY', team, { x: side * (pitch.length / 2 - pitch.penaltySpotDist), y: 0 });
}

/** Penalty placement (§6.2): defending GK on the goal line; taker + GK are the only players inside the box. */
export function placePenalty(state: MatchState, cfg: SimConfig): void {
  if (!state.restart || state.restart.kind !== 'PENALTY') return;
  const pitch = PITCH[state.mode.teamSize];
  const takerTeam = state.restart.team;
  const defTeam: TeamIdx = takerTeam === 0 ? 1 : 0;
  const side = state.attackDir[takerTeam];
  const goalLineX = (side * pitch.length) / 2;
  const boxEdgeX = side * (pitch.length / 2 - pitch.penaltyBoxDepth);
  const spot = state.restart.spot;

  const gk = state.players.find((p) => p.team === defTeam && p.isGk && !p.sentOff);
  if (gk) {
    // D122: he is pinned to his LINE, not to a spot. x is snapped (and only x-velocity killed) so he
    // cannot come off it; y is his own, so a human keeper can finally shuffle along the goal and read
    // the taker. D88's bot guess drives the same y and is untouched.
    gk.pos = { x: goalLineX, y: clamp(gk.pos.y, -pitch.goalWidth / 2, pitch.goalWidth / 2) };
    gk.vel = { x: 0, y: gk.vel.y };
  }
  // D62: the taker is the DESIGNATED one (state.restart.taker), not a second choice re-derived here
  // every tick. When the two disagreed, the designated taker — the only entity whose kick or claim the
  // sim accepts — was shoved back out of the box with his velocity zeroed on every tick, so the penalty
  // could not be taken until the restart timed out. Fall back to nearest only if there is no designation.
  let taker: PlayerState | undefined;
  const designated = state.restart?.taker;
  if (designated !== null && designated !== undefined) {
    const d = state.players[designated];
    if (d && d.team === takerTeam && !d.sentOff) taker = d;
  }
  if (!taker) {
    for (const p of state.players) {
      if (p.team !== takerTeam || p.sentOff) continue;
      if (!taker || Math.hypot(p.pos.x - spot.x, p.pos.y - spot.y) < Math.hypot(taker.pos.x - spot.x, taker.pos.y - spot.y)) taker = p;
    }
  }
  // D88: stand the taker a step BEHIND the ball, facing goal (a penalty is placed, not walked up to) — the
  // sim never teleported a penalty taker to the spot, so a bot/human taker had to trek across the pitch and
  // the penalty often timed out. Positioned within reach so his charged SHOOT connects. Rooted actions keep.
  // D122: placement happened once, at award (startRestart). All that is left is a CLAMP, and only
  // while the ceremony runs: the taker roams a pocket behind the spot — run-up room — and may not get
  // goal-side of the ball. Once the whistle goes the clamp lifts entirely: walk up and strike it.
  if (taker && inCeremony(state, cfg)) {
    const pocket = cfg.rules.PENALTY_TAKER_POCKET;
    const dx = taker.pos.x - spot.x;
    const dy = taker.pos.y - spot.y;
    const d = Math.hypot(dx, dy);
    if (d > pocket) {
      taker.pos.x = spot.x + (dx / d) * pocket;
      taker.pos.y = spot.y + (dy / d) * pocket;
    }
    if (side * (taker.pos.x - spot.x) > 0) {
      taker.pos.x = spot.x; // never ahead of the ball before the whistle
      if (side * taker.vel.x > 0) taker.vel.x = 0;
    }
  }
  // everyone else is excluded from the penalty box until the kick
  for (const p of state.players) {
    if (p.sentOff || p === gk || p === taker) continue;
    const inBox = side * p.pos.x > side * boxEdgeX && Math.abs(p.pos.y) < pitch.penaltyBoxHalfWidth;
    if (inBox) {
      p.pos.x = boxEdgeX - side * cfg.rules.PENALTY_BOX_MARGIN;
      p.vel = { x: 0, y: 0 };
    }
  }
}

/** The ball is "live" once the taker has kicked it (moving) or gained possession (§6.1 quick restart). */
export function ballLive(state: MatchState, cfg: SimConfig): boolean {
  if (state.ball.possessor !== null) return true;
  const speed = Math.hypot(state.ball.vel.x, state.ball.vel.y);
  return speed > cfg.rules.TRAPPED_SPEED;
}

/** Step 1 — count timed phases down; a GOAL_CELEBRATION expiry starts the conceding team's kickoff (§6.1). */
export function advancePhase(state: MatchState, events: SimEvent[], cfg: SimConfig): void {
  const timed =
    state.phase === 'GOAL_CELEBRATION' ||
    state.phase === 'HALFTIME' ||
    state.phase === 'KICKOFF' ||
    state.phase === 'RESTART_PENDING';
  if (timed && state.phaseTicksLeft > 0) state.phaseTicksLeft -= 1;

  if (state.phase === 'GOAL_CELEBRATION' && state.phaseTicksLeft === 0) {
    startKickoff(state, state.restart?.team ?? (0 as TeamIdx), events, cfg); // conceding team stored in restart
  } else if (state.phase === 'HALFTIME' && state.phaseTicksLeft === 0) {
    state.half = (state.half + 1) as 1 | 2 | 3; // 1 → 2 (sides already flipped at HALF_END)
    state.clockTicks = 0;
    startKickoff(state, state.restart?.team ?? (1 as TeamIdx), events, cfg); // second-half kickoff = the other team
  } else if ((state.phase === 'KICKOFF' || state.phase === 'RESTART_PENDING') && state.phaseTicksLeft === 0) {
    // untaken-restart auto-timeout (§6.1): nobody took it in the window ⇒ the ball goes live where it sits,
    // claimable by the nearest player. Prevents a live match freezing at a stoppage (M4-deferred gap).
    state.restart = null;
    setPhase(state, 'PLAY', 0, events);
  }
}
